/* eslint-disable @typescript-eslint/ban-ts-comment */
import {
  Button,
  Card,
  MessageBlock,
  NavList,
  Paragraph,
} from '@deckee/deck-hand';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { captureEvent } from '../../../../../services/event-capture';
import { hasFeatureEnabled } from '../../../../../services/feature';
import {
  backgroundGeolocationStart,
  backgroundGeolocationStop,
  checkLocationPermission,
  dismissBackgroundGeolocationDisclosed,
  isBackgroundGeolocationDisclosed,
} from '../../../../../services/log-trips/background-geolocation';
import {
  selectLocationConfig,
  updateLocationSharingSettings,
} from '../../../../../services/store/modules/app-controls';
import {
  fetchLoggedInMembersPositions,
  getSharedSignalsByCurrentMember,
  getSignalForCurrentMember,
  getSignalSharedByCurrentMember,
  removeShareFromMember,
} from '../../../../../services/store/modules/signal';
import { SheetBody } from '../../../../layouts/SheetLayout/SheetLayout';
import Shimmer from '../../../../shared/Shimmer/Shimmer';
import { ReactSvg, share } from '../../../../shared/vector-images';
import CheckSwitch from '../../../../shared/_deprecated/components/CheckSwitch/CheckSwitch';
import { SignalAppearanceTypes } from '../../components/SignalAppearanceChoices/SignalAppearance.types';
import SignalAppearanceChoices from '../../components/SignalAppearanceChoices/SignalAppearanceChoices';
import UserShareItem from '../../components/UserShareItem/UserShareItem';
import useNativeShare from '../../hooks/use-native-share';

const DECKEE_SIGNAL_TEXT =
  'DECKEE detects when you’re on the water and shares your Signal on the map based on your preference.';

const DECKEE_SAFETY_CONTACT_TEXT =
  'Your friends can always see you when you are on the water.';

const DECKEE_ON_WATER_DETECTION_TEXT =
  "Automatically record your activities on the water so you don't forget.";

const SignalActivateContainer: FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const topOfSelectedContactsListRef = useRef<HTMLHeadingElement>(null);

  const { signalMapSetting, onTheWaterDetectionEnabled } =
    useSelector(selectLocationConfig);

  const [contactBeingRemoved, setContactBeingRemoved] = useState<string | null>(
    null,
  );

  const [enablingOnTheWaterDetection, setEnablingOnTheWaterDetection] =
    useState(false);
  const [error, setError] = useState(false);

  const signalSharedByMember = useSelector(getSignalSharedByCurrentMember);
  const currentMembersPosition = useSelector(getSignalForCurrentMember);

  useEffect(() => {
    dispatch(getSharedSignalsByCurrentMember());
    dispatch(fetchLoggedInMembersPositions());
  }, [dispatch]);

  const theme = useTheme();

  const [canShare, triggerShare] = useNativeShare();

  const handleShareSignal = async () => {
    if (!currentMembersPosition) {
      throw new Error('No position for current member');
    }
    const sharePath = `/maps/signal/${currentMembersPosition.id}`;
    const title = 'Follow my location on the water with Deckee';
    const description = `Follow my location on the water with Deckee`;

    triggerShare({
      description,
      title,
      sharePath,
      eventName: 'signal: share my signal position link',
    });
  };

  const handleEnableOnTheWaterDetection = async (enabled = true) => {
    setEnablingOnTheWaterDetection(true);

    if (enabled) {
      captureEvent({
        type: 'signal: enable on the water detection',
        payload: {
          enabled,
        },
        sendToAmplitude: true,
      });
    } else {
      captureEvent({
        type: 'signal: disable on the water detection',
        payload: {
          enabled,
        },
        sendToAmplitude: true,
      });
    }

    await dispatch(
      updateLocationSharingSettings({
        onTheWaterDetectionEnabled: enabled,
      }),
    );

    const disclosureState = await isBackgroundGeolocationDisclosed();
    const permissionGranted = await checkLocationPermission();
    if (enabled) {
      if (disclosureState) {
        if (permissionGranted) {
          await backgroundGeolocationStart({
            lowFrequencyTracking: true,
            force: true,
          });
        } else {
          // Scenario: After our prompt and settings are NOT "always"
          // Given an existing user
          // And they are seeing the OTW prompt
          // When when they dismiss that prompt
          // Then they will be prompted with the native OS location prompt
          dismissBackgroundGeolocationDisclosed();
          history.push('/maps/on-the-water-permissions');
        }
      } else {
        history.push('/maps/on-the-water-permissions');
      }
    } else {
      await backgroundGeolocationStop();
    }

    setEnablingOnTheWaterDetection(false);
  };

  const handleRemoveContact = async (memberId: string) => {
    setError(false); // Reset any errors before trying again
    setContactBeingRemoved(memberId);

    captureEvent({
      type: 'signal: remove selected contact',
      payload: {
        memberId,
      },
      sendToAmplitude: true,
    });

    try {
      // @ts-ignore unwrap() is required so that the error is thrown correctly.
      await dispatch(removeShareFromMember(memberId)).unwrap();
    } catch (e) {
      // If there is an error scroll to the top of the contacts list so error is visible
      topOfSelectedContactsListRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });
      setError(true);
    } finally {
      setContactBeingRemoved(null);
    }
  };

  const handleAddNewContact = () => {
    captureEvent({
      type: 'signal: select add new contact',
      payload: {},
      sendToAmplitude: true,
    });
    history.push('/maps/signal/search');
  };

  const handleSelectAppearance = useCallback(
    (choice: SignalAppearanceTypes) => {
      const handleUpdateSignalMapSetting = async (
        setting: SignalAppearanceTypes,
      ) => {
        captureEvent({
          type: 'signal: update map setting',
          payload: {
            setting,
          },
          sendToAmplitude: true,
        });

        await dispatch(
          updateLocationSharingSettings({
            signalMapSetting: setting,
          }),
        );
      };

      handleUpdateSignalMapSetting(choice);
    },
    [dispatch],
  );

  return (
    <SheetBody overflow="auto">
      <Card>
        <Card.Body>
          <Paragraph as="div">{DECKEE_SIGNAL_TEXT}</Paragraph>

          <SignalAppearanceChoices
            activeChoice={signalMapSetting}
            onSelectChoice={handleSelectAppearance}
          />
        </Card.Body>
      </Card>

      {hasFeatureEnabled('on-the-water-detection') && (
        <Card>
          <Card.Heading fontWeight="bold" color="black">
            Automatic logbook
          </Card.Heading>

          <Card.Body
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Paragraph maxWidth="80%" as="div">
              {DECKEE_ON_WATER_DETECTION_TEXT}
            </Paragraph>

            <Shimmer on={enablingOnTheWaterDetection}>
              <CheckSwitch
                action={() =>
                  handleEnableOnTheWaterDetection(!onTheWaterDetectionEnabled)
                }
                isActive={onTheWaterDetectionEnabled}
              />
            </Shimmer>
          </Card.Body>
        </Card>
      )}

      {signalMapSetting !== SignalAppearanceTypes.Invisible && canShare && (
        <NavList>
          <NavList.Item onClick={handleShareSignal}>
            <Paragraph pl="1" fontWeight="bold" as="div" flex="1 1 0px">
              Share your location
            </Paragraph>

            <ReactSvg
              toDisplay={share()}
              options={{
                fillColor: theme.colors.primary,
              }}
              width={24}
              height={24}
            />
          </NavList.Item>
        </NavList>
      )}

      <Card>
        <Card.Heading ref={topOfSelectedContactsListRef}>Friends</Card.Heading>
        <Card.Body>
          <Paragraph mb="3" as="div">
            {DECKEE_SAFETY_CONTACT_TEXT}
          </Paragraph>
          {error && (
            <MessageBlock mb={2} type="error">
              There was an error removing the contact. Please try again.
            </MessageBlock>
          )}

          {signalSharedByMember.map((shareItem) => (
            <UserShareItem
              key={shareItem.id}
              padded
              displayName={shareItem.sharedWith.username}
              userImageId={shareItem.sharedWith.image}
              memberId={shareItem.id}
              buttonAction={handleRemoveContact}
              subscriber={shareItem.sharedWith.subscribed}
              contactBeingRemoved={contactBeingRemoved}
            />
          ))}

          <Button
            onClick={handleAddNewContact}
            variant="ghost"
            wide
            name="add-safety-contact"
          >
            Add friend
          </Button>
        </Card.Body>
      </Card>
    </SheetBody>
  );
};

export default SignalActivateContainer;
